import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type { BackendTypes } from '@tf/api';
import { FormBuilder, type FormControl } from '@tf/form-builder';
import type { ExtendedFormDefinition } from '@tf/shared';
import {
	Box,
	Button,
	createModal,
	Flex,
	Icon,
	ModalFooter,
	Stepper,
	TFLoadingOverlay,
	TFNotifier,
	useModalControl,
} from '@tf/ui';
import type { SubmitHandler } from '@tf/utils';
import { T } from '@tf/utils';
import type React from 'react';
import { useRef, useState } from 'react';
import { ErrorCard, FormLoadingCard } from '@/components/shared';
import { TFStepper } from '@/components/shared/TFStepper';
import { useSegmentsMutation, useSegmentsQuery } from '@/core/api';
import { useAppEnv, useDefinitions } from '@/core/providers';

export const FormModalContent = ({
	form,
	customSubmitButtonRenderer,
	customLoader,
	formControlRef,
	withSuccessMessage = true,
}: {
	form: ExtendedFormDefinition<BackendTypes.FormDefinition>;
	customSubmitButtonRenderer?: ((isLoading: boolean) => React.ReactNode) | undefined;
	customLoader?: React.ReactElement;
	formControlRef?: React.MutableRefObject<FormControl | null>;
	withSuccessMessage?: boolean;
}) => {
	const { onClose } = useModalControl();

	const appEnv = useAppEnv();
	const definitions = useDefinitions();

	const segmentsQuery = useSegmentsQuery(form.segmentIdentities);

	const segmentsMutation = useSegmentsMutation();

	const onSubmit: SubmitHandler = async ({ values, segmentValidationStatuses }) => {
		try {
			T.assertNotNullish(segmentsQuery.data, 'no segment containers loaded');

			const data: BackendTypes.SegmentsWritePayload = segmentsQuery.data.map((sc) => {
				const segmentKind = sc.container.info.SegmentIdentity.segmentKind;

				return {
					segment: values[segmentKind],
					validationStatus: segmentValidationStatuses[segmentKind],
					segmentIdentity: sc.container.info.SegmentIdentity,
					revision: sc.container.info.Revision,
				};
			});

			await segmentsMutation.mutateAsync({ data });

			const hasErrors = Object.values(segmentValidationStatuses).some((s) => s === 'VALIDATION_ERRORS');
			if (!hasErrors) {
				if (withSuccessMessage) {
					TFNotifier.success('Changes saved');
				}
				onClose();
			}
		} catch (_e) {
			TFNotifier.error('Failed to save changes');
		}
	};

	if (segmentsQuery.isPending) {
		return customLoader || <FormLoadingCard />;
	}

	if (segmentsQuery.error) {
		return (
			<ErrorCard
				icon={<Icon.IconExclamationCircle />}
				title="Failed to load form"
				description="Please try again"
				primaryAction={
					<Button mt="1rem" type="button" onClick={() => segmentsQuery.refetch()}>
						Reload form
					</Button>
				}
			/>
		);
	}

	return (
		<FormBuilder
			formControlRef={formControlRef}
			currency={definitions.currency}
			segments={segmentsQuery.data.map((sc) => {
				return {
					segmentKind: sc.container.info.SegmentIdentity.segmentKind,
					graphId: sc.container.info.SegmentIdentity.graphId,
					accessMode: sc.container.info.AccessMode,
				};
			})}
			initialValues={segmentsQuery.data.reduce(
				(acc, sc) => {
					return {
						...acc,
						[sc.container.info.SegmentIdentity.segmentKind]: sc.container.segment,
					};
				},
				{} as Record<BackendTypes.SegmentKind, BackendTypes.Segment>,
			)}
			validateInitialValues={segmentsQuery.data.some((s) => s.container.validationStatus === 'VALIDATION_ERRORS')}
			apiConfig={{
				username: '',
				clusterId: appEnv.clusterId,
			}}
			processingScripts={definitions.scripts}
			defs={{
				structs: definitions.defs.structs,
				options: definitions.defs.options,
			}}
			styles={{ content: { padding: '.5rem .75rem .25rem' }, segment: { marginTop: '0.75rem' } }}
			submitButtonRenderer={(isLoading) => {
				return customSubmitButtonRenderer ? (
					customSubmitButtonRenderer(isLoading)
				) : (
					<ModalFooter onCancel={onClose} isDisabled={isLoading} submitLabel="Save changes" />
				);
			}}
			onSubmit={onSubmit}
		/>
	);
};

interface Props {
	forms: ExtendedFormDefinition<BackendTypes.FormDefinition>[];
	onClose: () => void;
}

export const FormModal = createModal(({ onClose, forms }: Props) => {
	const [step, setStep] = useState<number>(0);
	const formControlRef = useRef<FormControl | null>(null);
	const isLastStep = step === forms.length - 1;
	const modalControl = useModalControl();
	const { _ } = useLingui();

	// ensures modal does not close automatically after submitting the first steps form
	modalControl.onClose = isLastStep ? onClose : () => {};

	return (
		<>
			<Flex sx={{ background: '#F8FAFC' }} mt="16px" justify="center">
				{forms.length > 1 && (
					<TFStepper maxScrollAreaWidth={600} stepsCount={forms.length} onStepChange={setStep} step={step}>
						{forms.map((form) => (
							<Stepper.Step key={form.name} label={form.label} />
						))}
					</TFStepper>
				)}
			</Flex>

			<Box mt="32px" sx={{ borderRadius: '8px', background: '#fff', overflow: 'hidden' }}>
				<FormModalContent
					withSuccessMessage={isLastStep}
					form={forms[step]}
					formControlRef={formControlRef}
					customSubmitButtonRenderer={(isLoading) => {
						return (
							<ModalFooter
								fullScreen
								isDisabled={isLoading}
								onSubmit={(e) => {
									const isValid = formControlRef.current?.validate();
									e.currentTarget.form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
									if (!isLastStep && isValid) {
										setStep((prev) => prev + 1);
									}
								}}
								submitLabel={
									isLastStep
										? _(msg({ id: 'formModal.FormModalContent.save', message: 'Save' }))
										: _(msg({ id: 'formModal.FormModalContent.continue', message: 'Continue' }))
								}
							/>
						);
					}}
					customLoader={<TFLoadingOverlay />}
				/>
			</Box>
		</>
	);
});
